import axios from "../../../shared/axios/axiosProxy.js";
import { CONTEXT_FIELD_RESOLVERS } from "./contextFieldResolvers.js";
import { normalizeEntryFlowSlug, normalizeFlowSlug } from "./flowSlug.js";

export const FLOW_ANALYTICS_ACTIONS = {
  STEP_ENTER: "step_enter",
  NAVIGATE_FORWARD: "navigate_forward",
  NAVIGATE_BACK: "navigate_back",
  REQ_SENT: "req_sent",
  FLOW_HANDOFF: "flow_handoff",
  ABANDON_HINT: "abandon_hint",
  CHOICE_RECORDED: "choice_recorded",
};

const pendingBySearchId = new Map();
const flushChains = new Map();

const runSerializedFlush = (searchId, task) => {
  const previous = flushChains.get(searchId) || Promise.resolve();
  const next = previous.then(task, task);
  flushChains.set(
    searchId,
    next.catch(() => {}),
  );
  return next;
};

const flushQueueWorker = async (searchId) => {
  const queue = pendingBySearchId.get(searchId);
  if (!queue || queue.items.length === 0) {
    return;
  }

  const batch = queue.items.splice(0, queue.items.length);
  const snapshot = { ...queue.snapshot };
  queue.snapshot = {};

  try {
    await axios.post(
      `/api/search/${searchId}/flow-analytics/events`,
      { events: batch, snapshot },
      { headers: { "Content-Type": "application/json" } },
    );
  } catch (error) {
    console.warn(
      "[flowAnalytics] Failed to persist events:",
      error?.response?.data || error.message,
    );
    queue.items.unshift(...batch);
    queue.snapshot = { ...snapshot, ...queue.snapshot };
  } finally {
    if (queue.items.length > 0) {
      scheduleQueueFlush(searchId);
    }
  }
};

const scheduleQueueFlush = (searchId) => {
  void runSerializedFlush(searchId, () => flushQueueWorker(searchId));
};

const enqueue = (searchId, event, snapshot) => {
  if (!searchId) return;

  if (!pendingBySearchId.has(searchId)) {
    pendingBySearchId.set(searchId, { items: [], snapshot: {} });
  }

  const queue = pendingBySearchId.get(searchId);
  queue.items.push(event);
  if (snapshot) {
    queue.snapshot = { ...queue.snapshot, ...snapshot };
  }

  scheduleQueueFlush(searchId);
};

const postAnalyticsBatch = async (
  searchId,
  events,
  snapshot,
  { keepalive = false } = {},
) => {
  if (!searchId || !events.length) {
    return;
  }

  const payload = { events, snapshot };
  const url = `/api/search/${searchId}/flow-analytics/events`;

  try {
    if (keepalive) {
      const body = JSON.stringify(payload);

      if (typeof fetch === "function") {
        const response = await fetch(url, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body,
          keepalive: true,
        });
        if (!response.ok) {
          throw new Error(`Analytics keepalive flush failed: HTTP ${response.status}`);
        }
        return response;
      }

      if (
        typeof navigator !== "undefined" &&
        typeof navigator.sendBeacon === "function"
      ) {
        const blob = new Blob([body], { type: "application/json" });
        if (!navigator.sendBeacon(url, blob)) {
          throw new Error("Analytics sendBeacon flush failed");
        }
        return;
      }

      console.warn(
        "[flowAnalytics] keepalive unavailable; terminal batch may be lost on navigation",
      );
    }

    return await axios.post(url, payload, {
      headers: { "Content-Type": "application/json" },
    });
  } catch (error) {
    console.warn(
      "[flowAnalytics] Failed to persist batch:",
      error?.response?.data || error.message || error,
    );
    throw error;
  }
};

const resolveContextFieldKeys = (stepRegistry, { stepIds = [], includeAll = false } = {}) => {
  if (includeAll) {
    return Object.keys(CONTEXT_FIELD_RESOLVERS);
  }

  const keys = new Set();
  for (const stepId of stepIds) {
    for (const key of stepRegistry.getStepContextFieldKeys(stepId)) {
      keys.add(key);
    }
  }
  return [...keys];
};

const pickContextValues = (flowInstance, fieldKeys) => {
  if (!flowInstance || fieldKeys.length === 0) {
    return {};
  }

  const context = {};
  for (const key of fieldKeys) {
    const resolve = CONTEXT_FIELD_RESOLVERS[key];
    if (!resolve) continue;

    const value = resolve(flowInstance);
    if (key === "cegjelzoSelected") {
      context[key] = value;
      continue;
    }
    if (value !== "" && value != null) {
      context[key] = value;
    }
  }
  return context;
};

/**
 * Build step-scoped context: only fields relevant to the given step(s).
 * On navigation, pass both fromStepId and toStepId so committed decisions
 * (e.g. fillingMethod) are captured when leaving a decision step.
 */
const buildAnalyticsContext = (stepRegistry, flowInstance, options = {}) => {
  const fieldKeys = resolveContextFieldKeys(stepRegistry, options);
  return pickContextValues(flowInstance, fieldKeys);
};

/**
 * Generic flow-analytics event tracker shared by every ASP wireframe flow
 * (searchFlowVersionB, regFlowVersionB, kftRegFlowVersionB, vafFlowVersionB).
 * All flows append to the SAME `search.flowAnalytics` document via searchId,
 * so `stepRegistry` is the only thing that differs per flow — funnelPath and
 * events accumulate across flow boundaries automatically.
 */
export const createFlowAnalyticsTracker = ({
  getSearchId,
  getSteps,
  getFlowInstance,
  entryFlow = "asp",
  stepRegistry,
}) => {
  let sessionStarted = false;
  let lastTrackedStepId = null;

  const resolveSearchId = () => {
    const id = getSearchId?.();
    return id && id !== "undefined" && id !== "null" ? String(id) : "";
  };

  const baseSnapshot = () => ({
    entryFlow: normalizeEntryFlowSlug(entryFlow),
  });

  const trackEvents = (events, snapshot = {}) => {
    const searchId = resolveSearchId();
    if (!searchId) return;

    for (const event of events) {
      enqueue(searchId, event, snapshot);
    }
  };

  const trackSessionStart = (stepIndex = 0) => {
    if (sessionStarted) return;
    sessionStarted = true;

    const steps = getSteps?.() || [];
    const meta = stepRegistry.getStepMetaAtIndex(steps, stepIndex);
    lastTrackedStepId = meta.id;
    const flowInstance = getFlowInstance?.();
    const context = buildAnalyticsContext(stepRegistry, flowInstance, { stepIds: [meta.id] });
    const at = new Date().toISOString();

    trackEvents(
      [
        {
          at,
          action: FLOW_ANALYTICS_ACTIONS.STEP_ENTER,
          stepId: meta.id,
          direction: "none",
          context,
        },
      ],
      baseSnapshot(),
    );
  };

  const trackStepEnterCatchUp = (stepIndex) => {
    const steps = getSteps?.() || [];
    const meta = stepRegistry.getStepMetaAtIndex(steps, stepIndex);
    if (!sessionStarted || lastTrackedStepId === meta.id) {
      return;
    }

    trackEvents([
      {
        at: new Date().toISOString(),
        action: FLOW_ANALYTICS_ACTIONS.STEP_ENTER,
        stepId: meta.id,
        direction: "none",
        context: buildAnalyticsContext(stepRegistry, getFlowInstance?.(), { stepIds: [meta.id] }),
      },
    ]);
    lastTrackedStepId = meta.id;
  };

  const trackNavigation = ({ fromStepIndex, toStepIndex, direction }) => {
    const steps = getSteps?.() || [];
    const fromMeta = stepRegistry.getStepMetaAtIndex(steps, fromStepIndex);
    const toMeta = stepRegistry.getStepMetaAtIndex(steps, toStepIndex);
    const context = buildAnalyticsContext(stepRegistry, getFlowInstance?.(), {
      stepIds: [fromMeta.id, toMeta.id],
    });
    const action =
      direction === "back"
        ? FLOW_ANALYTICS_ACTIONS.NAVIGATE_BACK
        : FLOW_ANALYTICS_ACTIONS.NAVIGATE_FORWARD;

    trackEvents(
      [
        {
          at: new Date().toISOString(),
          action,
          stepId: toMeta.id,
          fromStepId: fromMeta.id,
          toStepId: toMeta.id,
          direction,
          context,
        },
      ],
      baseSnapshot(),
    );

    if (lastTrackedStepId !== toMeta.id) {
      trackEvents([
        {
          at: new Date().toISOString(),
          action: FLOW_ANALYTICS_ACTIONS.STEP_ENTER,
          stepId: toMeta.id,
          direction: "none",
          context: buildAnalyticsContext(stepRegistry, getFlowInstance?.(), {
            stepIds: [toMeta.id],
          }),
        },
      ]);
    }
    lastTrackedStepId = toMeta.id;
  };

  const buildTerminalEvent = ({
    action,
    exitFlow,
    exitOutcome,
    extraContext = {},
    reqId,
  }) => {
    const steps = getSteps?.() || [];
    const flowInstance = getFlowInstance?.();
    const stepIndex = flowInstance?.state?.activeStep ?? -1;
    const meta = stepRegistry.getStepMetaAtIndex(steps, stepIndex);
    const normalizedExit = normalizeFlowSlug(exitFlow);

    const terminalContext = {
      ...buildAnalyticsContext(stepRegistry, flowInstance, { includeAll: true }),
      ...extraContext,
      ...(exitOutcome ? { exitOutcome } : {}),
      ...(reqId ? { reqId: String(reqId) } : {}),
    };
    if (normalizedExit) {
      terminalContext.exitFlow = normalizedExit;
    }

    const terminalSnapshot = {
      ...baseSnapshot(),
      ...(exitOutcome ? { exitOutcome } : {}),
    };
    if (normalizedExit) {
      terminalSnapshot.exitFlow = normalizedExit;
    }

    return {
      event: {
        at: new Date().toISOString(),
        action,
        stepId: meta.id,
        direction: "none",
        context: terminalContext,
      },
      snapshot: terminalSnapshot,
    };
  };

  const flushTerminalBatch = (terminalEvent, snapshot, { keepalive = false } = {}) => {
    const searchId = resolveSearchId();
    if (!searchId) {
      return;
    }

    void runSerializedFlush(searchId, async () => {
      const queue = pendingBySearchId.get(searchId);
      const pendingEvents = queue?.items?.length ? [...queue.items] : [];
      const mergedSnapshot = {
        ...(queue?.snapshot || {}),
        ...snapshot,
      };
      const batch = [...pendingEvents, terminalEvent];

      if (queue) {
        queue.items = [];
        queue.snapshot = {};
      }

      try {
        await postAnalyticsBatch(searchId, batch, mergedSnapshot, { keepalive });
      } catch {
        if (queue) {
          queue.items.unshift(...batch);
          queue.snapshot = { ...mergedSnapshot, ...queue.snapshot };
          scheduleQueueFlush(searchId);
        }
      }
    });
  };

  /** ASP (or EV/company) request successfully created — fires on contact step, before success screen. */
  const trackReqSent = ({
    exitFlow,
    reqId,
    exitOutcome = "submitted",
    extraContext = {},
  }) => {
    const { event, snapshot } = buildTerminalEvent({
      action: FLOW_ANALYTICS_ACTIONS.REQ_SENT,
      exitFlow,
      exitOutcome,
      extraContext,
      reqId,
    });
    flushTerminalBatch(event, snapshot);
  };

  /**
   * User's final "book consultation vs. continue" choice at one of the
   * consultation-booking interstitials. Fired from every branch of every
   * interstitial's handler, regardless of whether that branch also happens
   * to trigger a navigation event — see consultationChoice plan.
   */
  const trackChoice = ({ stepId, context = {} }) => {
    trackEvents(
      [
        {
          at: new Date().toISOString(),
          action: FLOW_ANALYTICS_ACTIONS.CHOICE_RECORDED,
          stepId,
          direction: "none",
          context,
        },
      ],
      baseSnapshot(),
    );
  };

  /** Redirect to another flow (reg/vaf/kftreg/asp) — uses keepalive so the batch survives navigation. */
  const trackFlowHandoff = ({ exitFlow, extraContext = {} }) => {
    const { event, snapshot } = buildTerminalEvent({
      action: FLOW_ANALYTICS_ACTIONS.FLOW_HANDOFF,
      exitFlow,
      exitOutcome: "handoff",
      extraContext,
    });
    flushTerminalBatch(event, { ...snapshot, exitOutcome: "handoff" }, {
      keepalive: true,
    });
  };

  /** Page unload without terminal event — uses keepalive so the batch survives tab close. */
  const trackAbandonHint = () => {
    const { event, snapshot } = buildTerminalEvent({
      action: FLOW_ANALYTICS_ACTIONS.ABANDON_HINT,
      exitOutcome: "abandoned",
    });
    flushTerminalBatch(event, snapshot, { keepalive: true });
  };

  return {
    trackSessionStart,
    trackStepEnterCatchUp,
    trackNavigation,
    trackChoice,
    trackReqSent,
    trackFlowHandoff,
    trackAbandonHint,
  };
};
